// Centralizes GA4 loading and event tracking so analytics only ever runs in
// the actual production deployment. Loading gtag.js unconditionally (as
// index.html used to) meant every local dev session and Playwright run fired
// real events against the production GA4 property, polluting funnel numbers
// - see the 2026-08-07 spike where 131 of 133 "sign_up" events that day
// carried hostName "localhost".
//
// import.meta.env.PROD alone is NOT enough to detect this: it reflects
// Vite's build *mode*, not the deploy target. dev-app.juryboxapp.com is built
// via `build:dev` -> `vite build` (default mode "production"), so PROD is
// true there too. VITE_APP_ENVIRONMENT is the primitive main.jsx already uses
// for the same distinction (see its Sentry `environment` config) and is set
// explicitly per script in package.json (build:dev="development",
// build:prod="production") - that's the one that actually tracks the deploy
// target, so both checks are required.
const ANALYTICS_ENABLED =
  import.meta.env.PROD && import.meta.env.VITE_APP_ENVIRONMENT === 'production';

const MEASUREMENT_ID = 'G-GZ5QR0LTKN';

let initialized = false;

export function initAnalytics() {
  if (initialized || !ANALYTICS_ENABLED) {
    return;
  }
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  // send_page_view is off in the initial config call because this is an SPA:
  // trackPageView (called from Page/index.jsx on every route change,
  // including the first) is the single source of page_view events instead of
  // double-counting the landing route.
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

export function trackEvent(name, params = {}) {
  if (!ANALYTICS_ENABLED || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', name, params);
}

// Routes carrying a single-use secret in the path itself (see
// components/Routes/index.jsx) - reached by direct landing from an emailed
// link, not client navigation, so this doesn't change what GA4 can observe,
// but this is the one place the full URL is now built by hand, which makes
// it the cheap spot to keep the token out of page_location. Order matters:
// the /signup/verify/:token pattern must be checked before the more general
// /signup/:token one, or it would match only up through "verify" and leave
// the token appended, unredacted.
const TOKEN_PATH_PATTERNS = [
  [/^\/resetpassword\/[^/?#]+/, '/resetpassword/<redacted>'],
  [/^\/signup\/verify\/[^/?#]+/, '/signup/verify/<redacted>'],
  [/^\/signup\/[^/?#]+/, '/signup/<redacted>'],
];

function redactTokenFromPath(path) {
  const match = TOKEN_PATH_PATTERNS.find(([pattern]) => pattern.test(path));
  return match ? path.replace(match[0], match[1]) : path;
}

export function trackPageView(path) {
  // page_path isn't a real GA4 parameter - gtag derives the page from
  // page_location/page_title, which it reads off `document` automatically.
  // Passing page_location explicitly here keeps this call self-contained
  // rather than relying on document.location having already been updated by
  // the router by the time gtag reads it.
  trackEvent('page_view', { page_location: window.location.origin + redactTokenFromPath(path) });
}
